/**
 * CloseOutPanel — Phase A
 * Allows staff to select which already-uploaded documents constitute the
 * confirmed final deliverables for a matter close-out.
 *
 * On confirm: POST /api/production/records/{pid}/close-out/confirm-deliverables
 *   { document_ids: [...] }
 * The endpoint evaluates the billing gate, makes docs portal-visible, then
 * automatically triggers the Closing Letter → Review Request sequence.
 *
 * Phase A enhancements:
 *   F1 — Documents classified as production deliverables (Allison) are
 *        highlighted as "Recommended" and sorted to the top of the list.
 *        Tax year is shown where available.
 *        Documents already portal-visible are labelled "Already in portal."
 *   F2 — When the billing gate blocks delivery, an inline banner shows the
 *        invoice reference and reason code. The outstanding dollar balance is
 *        NOT shown here; staff must use the Finance module (financial RBAC).
 *
 * Management conditions enforced:
 *   • Opening this panel does NOT call close-out/initiate.
 *     Initiation is a separate, explicit staff action (start_close_out in Journey).
 *   • Outstanding invoice amounts are not exposed. Only invoice ID and reason.
 *   • All interactive and status elements carry data-testid attributes.
 */
import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

// Classifications Allison recognises as completed production deliverables.
// Used to surface "Recommended" badges — does not gate eligibility.
const DELIVERABLE_CLASSIFICATIONS = new Set([
  "us_individual_tax_return",
  "us_amended_return",
  "us_business_tax_return",
  "foreign_tax_return",
  "advisory_deliverable",
  "bookkeeping_deliverable",
]);

const PAYMENT_REASON_LABELS = {
  outstanding_balance:       "Outstanding invoice balance",
  final_invoice_not_issued:  "Final invoice not yet issued",
  uninvoiced_obligation:     "Uninvoiced billing obligation",
};

export default function CloseOutPanel({ clientId, productionId, onClose, onConfirmed }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  // paymentRequired: { invoiceId, reasonCode } — no dollar amount (financial RBAC)
  const [paymentRequired, setPaymentRequired] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    setLoadError(false);
    setPaymentRequired(null);
    api.get(`/workspace/${clientId}/documents`)
      .then((r) => {
        const list = Array.isArray(r.data)
          ? r.data
          : (r.data?.uploaded_files || []);
        // Eligibility: not deleted, not Not-Needed/Duplicate, not superseded.
        const eligible = list.filter(
          (d) =>
            !d.is_deleted &&
            d.review_status !== "not_needed" &&
            d.review_status !== "duplicate" &&
            d.current_version !== false
        );
        // F1: Sort recommended deliverables to the top.
        eligible.sort((a, b) => {
          const aRec = DELIVERABLE_CLASSIFICATIONS.has(a.classification);
          const bRec = DELIVERABLE_CLASSIFICATIONS.has(b.classification);
          return bRec - aRec;
        });
        setDocs(eligible);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [clientId]);

  const toggle = (docId) =>
    setSelected((prev) => ({ ...prev, [docId]: !prev[docId] }));

  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const handleConfirm = async () => {
    if (!selectedIds.length) {
      toast.warning("Select at least one document as a final deliverable.");
      return;
    }
    setBusy(true);
    setPaymentRequired(null);
    try {
      const r = await api.post(
        `/production/records/${productionId}/close-out/confirm-deliverables`,
        { document_ids: selectedIds }
      );
      if (r.data?.status === "payment_required") {
        // F2: Show invoice reference and reason — NOT the dollar amount.
        // Outstanding balance details are available in the Finance module.
        setPaymentRequired({
          invoiceId:  r.data.invoice_id  || null,
          reasonCode: r.data.reason_code || "outstanding_balance",
        });
        return;
      }
      toast.success(
        "Deliverables confirmed. Closing letter and review request will be sent automatically."
      );
      onConfirmed?.();
    } catch {
      toast.error("Could not confirm deliverables. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.FileCheck className="h-5 w-5 text-slate-600" />
            Confirm Final Deliverables
          </DialogTitle>
          <DialogDescription>
            Select the completed documents to deliver to the client through the secure portal.
            Only the documents you select will be confirmed as final deliverables.
            Once confirmed, the closing letter and review request will be sent automatically.
          </DialogDescription>
        </DialogHeader>

        {/* F2 — Payment required banner (invoice ID + reason, no dollar amount) */}
        {paymentRequired && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm"
            data-testid="closeout-payment-required-banner"
          >
            <div className="flex items-start gap-2">
              <Icons.AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-amber-900">
                  Payment required before delivery
                </p>
                <p className="text-amber-800 mt-0.5">
                  {PAYMENT_REASON_LABELS[paymentRequired.reasonCode] || "A payment obligation must be resolved."}
                </p>
                {paymentRequired.invoiceId && (
                  <p className="text-amber-700 mt-1 text-xs" data-testid="closeout-invoice-id">
                    Invoice reference:{" "}
                    <span className="font-mono font-semibold">
                      {paymentRequired.invoiceId}
                    </span>
                  </p>
                )}
                <p className="text-amber-700 mt-1 text-xs">
                  Contact Finance to resolve this invoice, then return here to confirm deliverables.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-2 max-h-72 overflow-y-auto space-y-2" data-testid="closeout-doc-list">
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
              <Icons.Loader2 className="h-4 w-4 animate-spin mr-2" />
              Loading documents…
            </div>
          )}
          {!loading && loadError && (
            <p
              className="text-sm text-red-600 py-4 text-center"
              data-testid="closeout-doc-load-error"
            >
              Unable to load documents. Please try again.
            </p>
          )}
          {!loading && !loadError && docs.length === 0 && (
            <p
              className="text-sm text-slate-500 py-4 text-center"
              data-testid="closeout-doc-empty"
            >
              No documents found for this client. Upload completed files first.
            </p>
          )}
          {!loading && !loadError && docs.map((doc) => {
            const isRecommended = DELIVERABLE_CLASSIFICATIONS.has(doc.classification);
            const isPortalVisible = doc.portal_visible === true;
            return (
              <label
                key={doc.id}
                htmlFor={`co-doc-${doc.id}`}
                className={[
                  "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                  isRecommended
                    ? "border-blue-200 bg-blue-50/40 hover:bg-blue-50"
                    : "border-slate-200 hover:bg-slate-50",
                ].join(" ")}
                data-testid={`closeout-doc-${doc.id}`}
              >
                <Checkbox
                  id={`co-doc-${doc.id}`}
                  checked={!!selected[doc.id]}
                  onCheckedChange={() => toggle(doc.id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {doc.original_filename || doc.filename || doc.id}
                    </p>
                    {/* F1 — Recommended badge for Allison-classified deliverables */}
                    {isRecommended && (
                      <Badge
                        variant="outline"
                        className="text-blue-700 border-blue-300 bg-blue-50 text-xs px-1.5 py-0"
                        data-testid={`closeout-doc-recommended-${doc.id}`}
                      >
                        Recommended
                      </Badge>
                    )}
                    {/* F1 — Already-in-portal badge */}
                    {isPortalVisible && (
                      <Badge
                        variant="outline"
                        className="text-emerald-700 border-emerald-300 bg-emerald-50 text-xs px-1.5 py-0"
                        data-testid={`closeout-doc-already-portal-${doc.id}`}
                      >
                        Already in portal
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <p className="text-xs text-slate-400">
                      {doc.document_type || doc.category || "Document"}
                    </p>
                    {/* F1 — Tax year from Allison extraction */}
                    {doc.tax_year && (
                      <p
                        className="text-xs text-slate-500"
                        data-testid={`closeout-doc-tax-year-${doc.id}`}
                      >
                        · Tax Year: {doc.tax_year}
                      </p>
                    )}
                    {doc.created_at && (
                      <p className="text-xs text-slate-300">
                        ·{" "}
                        {new Date(doc.created_at).toLocaleDateString(undefined, {
                          month: "short", day: "numeric", year: "numeric",
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {selectedIds.length > 0 && !paymentRequired && (
          <p className="text-xs text-slate-500 mt-1">
            {selectedIds.length} document{selectedIds.length !== 1 ? "s" : ""} selected as final
            deliverables. These will be made visible in the client portal once payment clears.
          </p>
        )}

        <DialogFooter className="mt-4 gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid="closeout-cancel"
            className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy || !selectedIds.length || loadError}
            data-testid="closeout-confirm-deliverables"
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
          >
            {busy && <Icons.Loader2 className="h-4 w-4 animate-spin" />}
            Confirm Final Deliverables
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
